import { useState } from "react";
import { api, apiError } from "../../lib/api";
import { Card } from "../ui/card";
import { PriorityBadge, Avatar } from "../badges";
import { TaskCreateDialog } from "./TasksTab";
import { toast } from "sonner";
import { Flag } from "lucide-react";

const COLUMNS = [
  { id: "todo", label: "To Do", accent: "border-t-slate-500" },
  { id: "in_progress", label: "In Progress", accent: "border-t-blue-500" },
  { id: "blocked", label: "Blocked", accent: "border-t-rose-500" },
  { id: "testing", label: "Testing", accent: "border-t-amber-500" },
  { id: "completed", label: "Completed", accent: "border-t-emerald-500" },
];

export default function KanbanTab({ project, tasks, requirements, users, reload, openTask }) {
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const userById = (id) => users.find((u) => u.id === id);

  const drop = async (status) => {
    setOverCol(null);
    if (!dragId) return;
    const t = tasks.find((x) => x.id === dragId);
    setDragId(null);
    if (!t || t.status === status) return;
    try {
      await api.patch(`/tasks/${t.id}`, { status });
      toast.success(`${t.tid} → ${status.replace("_", " ")}`);
      reload();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-4" data-testid="kanban-tab-content">
      <div className="flex justify-end">
        <TaskCreateDialog project={project} users={users} requirements={requirements} tasks={tasks} reload={reload} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-start">
        {COLUMNS.map((col) => {
          const items = tasks.filter((t) => t.status === col.id);
          return (
            <div key={col.id} data-testid={`kanban-column-${col.id}`}
              className={`rounded-md border border-border border-t-2 ${col.accent} bg-card/50 min-h-[200px] transition-colors ${overCol === col.id ? "bg-accent/60" : ""}`}
              onDragOver={(e) => { e.preventDefault(); setOverCol(col.id); }}
              onDragLeave={() => setOverCol(null)}
              onDrop={() => drop(col.id)}>
              <div className="px-3 py-2.5 flex items-center justify-between border-b border-border">
                <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{col.label}</span>
                <span className="text-[10px] font-mono text-muted-foreground bg-accent rounded px-1.5">{items.length}</span>
              </div>
              <div className="p-2 space-y-2">
                {items.map((t) => (
                  <Card key={t.id} draggable data-testid={`kanban-card-${t.tid.toLowerCase()}`}
                    onDragStart={() => setDragId(t.id)}
                    onClick={() => openTask(t.id)}
                    className={`p-3 cursor-grab active:cursor-grabbing hover:border-primary/50 transition-colors ${dragId === t.id ? "opacity-50" : ""}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-[10px] text-primary">
                        {t.milestone && <Flag className="h-3 w-3 inline mr-1 text-amber-500" />}{t.tid}
                      </span>
                      <PriorityBadge priority={t.priority} />
                    </div>
                    <p className="text-sm font-medium mt-1.5 leading-snug">{t.title}</p>
                    <div className="flex items-center justify-between mt-2.5">
                      {t.assignee_id ? <Avatar name={userById(t.assignee_id)?.name} size="xs" /> : <span />}
                      <span className="text-[10px] font-mono text-muted-foreground">{t.due_date || ""}</span>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
